import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuditLog, AuditLogStatistics } from 'pages/AuditLogs/types';
import { fetchAuditLogs } from './thunk';

interface AuditLogsState {
  data: AuditLog[];
  selectedItem: AuditLog | null;
  statistics: AuditLogStatistics | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  filters: {
    searchTerm: string;
    action?: string;
    module?: string;
    result?: string;
    userId?: number;
    fromDate?: string;
    toDate?: string;
    ipAddress?: string;
    entityType?: string;
  };
  sorting: {
    field: string;
    order: 'asc' | 'desc';
  };
  dashboardLoading: boolean;
  dashboardError: string | null;
}

const initialState: AuditLogsState = {
  data: [],
  selectedItem: null,
  statistics: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: {
    searchTerm: '',
  },
  sorting: {
    field: 'timestamp',
    order: 'desc',
  },
  dashboardLoading: false,
  dashboardError: null,
};

const auditLogsSlice = createSlice({
  name: 'AuditLogs',
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<AuditLog[]>) => {
      state.data = action.payload;
      state.loading = false;
    },
    setSelectedItem: (state, action: PayloadAction<AuditLog | null>) => {
      state.selectedItem = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setFilters: (state, action: PayloadAction<Partial<AuditLogsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = { searchTerm: '' };
    },
    setSorting: (state, action: PayloadAction<{ field: string; order: 'asc' | 'desc' }>) => {
      state.sorting = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPagination: (state, action: PayloadAction<Partial<AuditLogsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setStatistics: (state, action: PayloadAction<AuditLogStatistics>) => {
      state.statistics = action.payload;
      state.dashboardLoading = false;
    },
    setDashboardLoading: (state, action: PayloadAction<boolean>) => {
      state.dashboardLoading = action.payload;
    },
    setDashboardError: (state, action: PayloadAction<string>) => {
      state.dashboardError = action.payload;
      state.dashboardLoading = false;
    },
    clearSelectedItem: (state) => {
      state.selectedItem = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.data = payload.items || payload.data || payload || [];
        state.pagination.totalItems = payload.totalItems ?? payload.total ?? state.data.length;
        state.pagination.totalPages = payload.totalPages ?? Math.ceil(state.pagination.totalItems / state.pagination.pageSize);
        state.pagination.currentPage = payload.currentPage ?? state.pagination.currentPage;
        state.error = null;
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setData,
  setSelectedItem,
  setLoading,
  setError,
  setFilters,
  clearFilters,
  setSorting,
  setPage,
  setPagination,
  setStatistics,
  setDashboardLoading,
  setDashboardError,
  clearSelectedItem,
} = auditLogsSlice.actions;

export default auditLogsSlice.reducer;